import { useCallback, useState } from 'react';

import { FormObject } from '../../../@types';
import { CreateBulkPartActionsRequest } from '../../../@types/partAction.type';
import {
  CheckInObject,
  CheckOutObject,
  ServiceNodeAllocationData,
  ServiceStatusCode,
} from '../../../@types/service.type';
import { TaskTypeCategory, TaskData } from '../../../@types/task.type';
import { getServiceCheckIn } from '../../../api/checkin-checkout';
import { HttpMethod } from '../../../api/http-client';
import { saveServiceStatus } from '../../../api/services';
import { ServiceCancelOrReOpen } from '../../../configs/enums';
import { useCreateExceptionalPart } from '../../../queries/part-query';
import { useAddBulkPartActions } from '../../../queries/partAction-query';
import { useUpdateServiceAllocation } from '../../../queries/service-query';
import {
  useGetNextServicesStatuses,
  useGetServiceCancelOrReopenStatuses,
  useSaveServiceStatus,
} from '../../../queries/service-status-query';
import { useCreateAndUpdateTask, useGetTaskTypes } from '../../../queries/task-query';
import {
  getCurrentTimestamp,
  getCurrentTime,
  getTimestamp,
  getJSTimestamp,
  getDateFromTimestamp,
} from '../../../utils/common';
import { JobInteraction } from '../useListJobActions';

// Shape returned by any BFF endpoint that probes for interaction forms before
// committing (PUT /services/:id/status, POST /partAction/:id/bulkSubmit). When
// the probe fires, the BFF returns these optional fields instead of the
// committed resource. The API clients are typed with the commit shape, so we
// extract these fields via a narrow local type on the probe call site.
interface InteractionProbeResponse {
  interactionFormData?: Array<
    {
      id?: number;
      templateContent?: FormObject;
      templateIsMandatory?: boolean;
      templateIsRepeatable?: boolean;
    } & Partial<FormObject>
  >;
  actionTriggers?: unknown[];
  isCheckin?: boolean;
  multiOnSiteSuggestionsAvailable?: boolean;
}

const toPendingForms = (
  formData: NonNullable<InteractionProbeResponse['interactionFormData']>
): FormObject[] =>
  formData.map((obj) => ({
    ...((obj.templateContent ?? obj) as FormObject),
    ruleId: obj.id as number,
    isMandatory: obj.templateIsMandatory as boolean,
    isRepeatable: obj.templateIsRepeatable as boolean,
  }));

export interface ExceptionalPartInput {
  partTypeId: number;
  serial1: string;
  conditionCode: string;
  serviceId: number;
  partActionType: string;
  imageData: unknown[];
}

export interface PartActionSubmitInput {
  actions: CreateBulkPartActionsRequest[];
  exceptionalPart?: ExceptionalPartInput;
}

export interface TaskSubmitInput {
  taskTypeCode: string;
  taskTypeCategory?: TaskTypeCategory;
  serviceId: number;
  taskData?: TaskData;
  taskId?: number;
  execute?: boolean;
}

export const useServiceDialogs = (
  serviceId: number,
  statusCode: string,
  serviceTypeCode: string,
  locationNodeId: number,
  onRefresh: () => void
) => {
  const [lastError, setLastError] = useState<string | null>(null);

  // Status dialog
  const nextStatusesQuery = useGetNextServicesStatuses(statusCode, serviceTypeCode, serviceId);
  const saveStatusMutation = useSaveServiceStatus(serviceId);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [pendingInteractions, setPendingInteractions] = useState<JobInteraction[]>([]);

  // Check-in dialog — shown when probe returns isCheckin: true (nextStatus ons).
  // `forms` carries any interaction forms the probe returned alongside isCheckin:
  // the BFF interceptor re-matches rules on every request and only honours
  // byPassInteractionCheck when NO rules match, so a commit without the filled
  // forms bounces back as a 200 with the forms again and the status never lands.
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);
  const [pendingCheckInStatus, setPendingCheckInStatus] = useState<{
    statusCode: string;
    notes: string;
    forms: FormObject[];
  } | null>(null);
  // Declared here (not with the other cancel/futile state) so runStatusChange
  // can reuse the probe+commit path for cancel/futile too without a forward ref.
  const cancelFutileStatusesQuery = useGetServiceCancelOrReopenStatuses(
    ServiceCancelOrReOpen.CancelFutile,
    statusCode,
    serviceTypeCode,
    serviceId
  );
  const [cancelFutileDialogOpen, setCancelFutileDialogOpen] = useState(false);
  // Reopen uses the same BFF endpoint as cancel/futile, but with option=Re-Open.
  // Declared here (alongside the other status-mutation state) so runStatusChange
  // can drive it without a forward ref.
  const reopenStatusesQuery = useGetServiceCancelOrReopenStatuses(
    ServiceCancelOrReOpen.ReOpen,
    statusCode,
    serviceTypeCode,
    serviceId
  );
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);

  const handleOpenStatusDialog = useCallback(() => {
    if (!nextStatusesQuery.data || nextStatusesQuery.isStale) {
      nextStatusesQuery.refetch();
    }
    setStatusDialogOpen(true);
  }, [nextStatusesQuery]);

  // Run the probe + commit dance required by PUT /services/:id/status.
  // Probe returns interaction forms (if any). When there are none we commit
  // with byPassInteractionCheck: true — otherwise the BFF keeps re-probing
  // and the status change never lands (see Parts In Transit).
  // When the probe returns isCheckin: true the BFF expects checkinData in the
  // commit payload — open the check-in dialog to collect it first.
  const runStatusChange = useCallback(
    async (newStatusCode: string, notes: string): Promise<boolean> => {
      // Leaving On Site must close out the attendance record. The BFF only
      // does that when the commit carries checkoutData — and when it does,
      // the route handler delegates the status change itself to
      // processCheckout (checkout record update + AttendanceCheckOut log +
      // force-checkout of other users + status update).
      let checkoutData: CheckOutObject | undefined;
      if (statusCode === ServiceStatusCode.OnSite && newStatusCode !== ServiceStatusCode.OnSite) {
        try {
          // BFF GET /services/:id/checkins/:mode returns the check-in record(s)
          // for the service. The api client is typed as a single CheckInObject
          // but the endpoint returns an array, so normalise both shapes and
          // pick the latest record (highest id), mirroring the old frontend's
          // getCheckedInData.
          const response = (await getServiceCheckIn(serviceId)) as unknown as
            | { id: number }
            | Array<{ id: number }>
            | null;
          const checkins = Array.isArray(response) ? response : response ? [response] : [];
          const latest = checkins.reduce<{ id: number } | undefined>(
            (max, c) => (max && max.id > c.id ? max : c),
            undefined
          );
          checkoutData = {
            checkinId: latest?.id ?? 0,
            nextStatus: newStatusCode,
            notes,
            checkOutTime: getCurrentTimestamp(),
            actualCheckOutTime: getCurrentTime(),
          };
        } catch (err) {
          // Check-ins are permission-gated (service.edit.checkin_checkout.allow),
          // which the BFF rejects with 401/403. For that case fall back to a
          // plain status change — it's all an unpermitted user can do anyway,
          // and attaching checkoutData without the permission would make the
          // BFF skip both updateStatus and processCheckout, losing the change.
          // Any OTHER error (network / 5xx) must not be swallowed: skipping
          // checkoutData would change the status while leaving the attendance
          // record open. Re-throw so the caller surfaces it and can retry.
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status !== 401 && status !== 403) {
            throw err;
          }
        }
      }

      const payload = {
        nextStatus: newStatusCode,
        note: notes,
        byPassInteractionCheck: false,
        bulkCheckinData: [],
        ...(checkoutData && { checkoutData }),
      };
      const probe = (await saveServiceStatus(
        serviceId,
        payload as unknown as Parameters<typeof saveServiceStatus>[1]
      )) as unknown as InteractionProbeResponse;

      if (probe.isCheckin) {
        setPendingCheckInStatus({
          statusCode: newStatusCode,
          notes,
          forms: probe.interactionFormData?.length ? toPendingForms(probe.interactionFormData) : [],
        });
        setStatusDialogOpen(false);
        setCheckInDialogOpen(true);
        return false;
      }

      const formData = probe.interactionFormData;
      if (formData && formData.length > 0) {
        setPendingInteractions([
          {
            serviceId,
            forms: toPendingForms(formData),
            originalUrl: `/services/${serviceId}/status`,
            originalPayload: payload,
          },
        ]);
        return false;
      }

      await saveStatusMutation.mutateAsync({
        ...payload,
        byPassInteractionCheck: true,
      } as unknown as Parameters<typeof saveStatusMutation.mutateAsync>[0]);
      return true;
    },
    [serviceId, statusCode, saveStatusMutation]
  );

  const handleCheckInSubmit = useCallback(
    async (travelStart: string, onSiteTime: string, notes: string) => {
      if (!pendingCheckInStatus) return;
      setCheckInSubmitting(true);
      try {
        const checkInTime = onSiteTime ? getJSTimestamp(onSiteTime) : getCurrentTimestamp();
        const checkinData: CheckInObject = {
          serviceId,
          checkInTime,
          travelStartTime: travelStart ? getTimestamp(travelStart) : checkInTime,
          locationNodeId,
          notes,
          selectedServiceIds: [],
          actualCheckInTime: onSiteTime ? getDateFromTimestamp(onSiteTime) : getCurrentTime(),
        };
        const payload = {
          nextStatus: pendingCheckInStatus.statusCode,
          note: pendingCheckInStatus.notes,
          byPassInteractionCheck: true,
          checkinData,
          bulkCheckinData: [],
        };

        // The probe returned interaction forms alongside isCheckin — the commit
        // must carry the filled forms (isInterceptable check) or the BFF
        // interceptor re-sends them as a 200 without ever changing the status.
        // Hand the checkin-enriched payload to InteractionDialog, which submits
        // forms + payload together.
        if (pendingCheckInStatus.forms.length > 0) {
          setPendingInteractions([
            {
              serviceId,
              forms: pendingCheckInStatus.forms,
              originalUrl: `/services/${serviceId}/status`,
              originalPayload: payload,
            },
          ]);
          setCheckInDialogOpen(false);
          setPendingCheckInStatus(null);
          return;
        }

        const commit = (await saveStatusMutation.mutateAsync(
          payload as unknown as Parameters<typeof saveStatusMutation.mutateAsync>[0]
        )) as unknown as InteractionProbeResponse;

        // Bounce guard: a 200 that still contains interactionFormData means the
        // interceptor matched rules on the commit and the status did NOT change.
        // Surface the forms instead of reporting silent success.
        if (commit?.interactionFormData?.length) {
          setPendingInteractions([
            {
              serviceId,
              forms: toPendingForms(commit.interactionFormData),
              originalUrl: `/services/${serviceId}/status`,
              originalPayload: payload,
            },
          ]);
          setCheckInDialogOpen(false);
          setPendingCheckInStatus(null);
          return;
        }

        setCheckInDialogOpen(false);
        setPendingCheckInStatus(null);
        setStatusDialogOpen(false);
        onRefresh();
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Check-in failed');
      } finally {
        setCheckInSubmitting(false);
      }
    },
    [serviceId, locationNodeId, pendingCheckInStatus, saveStatusMutation, onRefresh]
  );

  const handleStatusSubmit = useCallback(
    async (newStatusCode: string, notes: string) => {
      setStatusSubmitting(true);
      try {
        const committed = await runStatusChange(newStatusCode, notes);
        if (committed) {
          setStatusDialogOpen(false);
          onRefresh();
        }
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Status change failed');
      } finally {
        setStatusSubmitting(false);
      }
    },
    [runStatusChange, onRefresh]
  );

  const clearPendingInteractions = useCallback(() => {
    setPendingInteractions([]);
    setStatusDialogOpen(false);
    setCancelFutileDialogOpen(false);
    setReopenDialogOpen(false);
    setPartActionDialogOpen(false);
    setCheckInDialogOpen(false);
    setPendingCheckInStatus(null);
    onRefresh();
  }, [onRefresh]);

  const cancelPendingInteractions = useCallback(() => {
    setPendingInteractions([]);
    setStatusDialogOpen(false);
    setCancelFutileDialogOpen(false);
    setReopenDialogOpen(false);
    setPartActionDialogOpen(false);
    setCheckInDialogOpen(false);
    setPendingCheckInStatus(null);
  }, []);

  // Allocation dialog
  const allocationMutation = useUpdateServiceAllocation(serviceId);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [allocationSubmitting, setAllocationSubmitting] = useState(false);

  const handleOpenAllocationDialog = useCallback(() => {
    setAllocationDialogOpen(true);
  }, []);

  const handleAllocationSubmit = useCallback(
    async (nodeId: number, allocationTypeCode: string) => {
      setAllocationSubmitting(true);
      try {
        const data: ServiceNodeAllocationData = {
          serviceId,
          userDepotAllocationNodeId: nodeId,
          allocationTypeCode,
        };
        await allocationMutation.mutateAsync(data);
        setAllocationDialogOpen(false);
        onRefresh();
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Allocation update failed');
      } finally {
        setAllocationSubmitting(false);
      }
    },
    [serviceId, allocationMutation, onRefresh]
  );

  // Part Action dialog
  const bulkPartActionMutation = useAddBulkPartActions(serviceId);
  const createExceptionalPartMutation = useCreateExceptionalPart();
  const [partActionDialogOpen, setPartActionDialogOpen] = useState(false);
  const [partActionSubmitting, setPartActionSubmitting] = useState(false);

  const handleOpenPartActionDialog = useCallback(() => {
    setPartActionDialogOpen(true);
  }, []);

  const handlePartActionSubmit = useCallback(
    async (input: PartActionSubmitInput): Promise<boolean> => {
      setPartActionSubmitting(true);
      try {
        let actions = input.actions;

        // Step 1: Create exceptional part first if needed
        if (input.exceptionalPart) {
          const exceptionalPayload = {
            partTypeId: input.exceptionalPart.partTypeId,
            serial1: input.exceptionalPart.serial1,
            conditionCode: input.exceptionalPart.conditionCode,
            serviceId: input.exceptionalPart.serviceId,
            exception: true,
            isAccepted: false,
            partActionType: input.exceptionalPart.partActionType,
            imageData: input.exceptionalPart.imageData,
            isActive: false,
          };
          const createdPart = await createExceptionalPartMutation.mutateAsync(exceptionalPayload);
          // Inject the created part ID into the action's installedOrMaintainedPartData
          actions = actions.map((a) => ({
            ...a,
            installedOrMaintainedPartData: {
              ...(a.installedOrMaintainedPartData as unknown as Record<string, unknown>),
              partId: (createdPart as Record<string, unknown>).id,
            } as unknown as JSON,
          }));
        }

        // Step 2: Probe-submit the part action. BFF returns interactionFormData
        // when the action triggers interaction forms instead of committing.
        const probe = (await bulkPartActionMutation.mutateAsync(
          actions
        )) as unknown as InteractionProbeResponse;
        const formData = probe?.interactionFormData;
        if (formData && formData.length > 0) {
          setPendingInteractions([
            {
              serviceId,
              forms: toPendingForms(formData),
              originalUrl: `/partAction/${serviceId}/bulkSubmit`,
              originalPayload: { partActionForms: actions as unknown as Record<string, unknown>[] },
              method: HttpMethod.Post,
            },
          ]);
          // Not committed yet — interaction forms must be completed first. Report
          // "not successful" so the dialog keeps the entered details.
          return false;
        }

        setPartActionDialogOpen(false);
        onRefresh();
        return true;
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Part action failed');
        return false;
      } finally {
        setPartActionSubmitting(false);
      }
    },
    [serviceId, bulkPartActionMutation, createExceptionalPartMutation, onRefresh]
  );

  // Cancel/Futile dialog
  const handleOpenCancelFutileDialog = useCallback(() => {
    cancelFutileStatusesQuery.refetch();
    setCancelFutileDialogOpen(true);
  }, [cancelFutileStatusesQuery]);

  const handleCancelFutileSubmit = useCallback(
    async (newStatusCode: string, notes: string) => {
      setStatusSubmitting(true);
      try {
        const committed = await runStatusChange(newStatusCode, notes);
        if (committed) {
          setCancelFutileDialogOpen(false);
          onRefresh();
        }
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Cancel/Futile failed');
      } finally {
        setStatusSubmitting(false);
      }
    },
    [runStatusChange, onRefresh]
  );

  // Reopen dialog — shares runStatusChange with the regular status flow so the
  // interaction-form probe still fires if the destination status triggers one.
  const handleOpenReopenDialog = useCallback(() => {
    reopenStatusesQuery.refetch();
    setReopenDialogOpen(true);
  }, [reopenStatusesQuery]);

  const handleReopenSubmit = useCallback(
    async (newStatusCode: string, notes: string) => {
      setStatusSubmitting(true);
      try {
        const committed = await runStatusChange(newStatusCode, notes);
        if (committed) {
          setReopenDialogOpen(false);
          onRefresh();
        }
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Reopen failed');
      } finally {
        setStatusSubmitting(false);
      }
    },
    [runStatusChange, onRefresh]
  );

  // Task dialog
  const taskMutation = useCreateAndUpdateTask(serviceId);
  const taskTypesQuery = useGetTaskTypes(serviceId);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  const handleOpenTaskDialog = useCallback(() => {
    if (!taskTypesQuery.data || taskTypesQuery.isStale) {
      taskTypesQuery.refetch();
    }
    setTaskDialogOpen(true);
  }, [taskTypesQuery]);

  const handleTaskSubmit = useCallback(
    async (task: TaskSubmitInput) => {
      setTaskSubmitting(true);
      try {
        await taskMutation.mutateAsync(task);
        setTaskDialogOpen(false);
        onRefresh();
      } catch (err) {
        setLastError(err instanceof Error ? err.message : 'Task creation failed');
      } finally {
        setTaskSubmitting(false);
      }
    },
    [taskMutation, onRefresh]
  );

  return {
    // Error state
    lastError,
    clearError: useCallback(() => setLastError(null), []),

    // Status
    statusDialogOpen,
    statusSubmitting,
    handleOpenStatusDialog,
    handleCloseStatusDialog: useCallback(() => setStatusDialogOpen(false), []),
    handleStatusSubmit,
    nextStatusesQuery,

    // Check-in (triggered when status probe returns isCheckin: true)
    checkInDialogOpen,
    checkInSubmitting,
    handleCheckInSubmit,
    handleCloseCheckInDialog: useCallback(() => {
      setCheckInDialogOpen(false);
      setPendingCheckInStatus(null);
    }, []),

    // Interaction forms triggered by a status probe
    pendingInteractions,
    clearPendingInteractions,
    cancelPendingInteractions,

    // Allocation
    allocationDialogOpen,
    allocationSubmitting,
    handleOpenAllocationDialog,
    handleCloseAllocationDialog: useCallback(() => setAllocationDialogOpen(false), []),
    handleAllocationSubmit,

    // Part Action
    partActionDialogOpen,
    partActionSubmitting,
    handleOpenPartActionDialog,
    handleClosePartActionDialog: useCallback(() => setPartActionDialogOpen(false), []),
    handlePartActionSubmit,

    // Cancel/Futile
    cancelFutileDialogOpen,
    handleOpenCancelFutileDialog,
    handleCloseCancelFutileDialog: useCallback(() => setCancelFutileDialogOpen(false), []),
    handleCancelFutileSubmit,
    cancelFutileStatusesQuery,

    // Reopen
    reopenDialogOpen,
    handleOpenReopenDialog,
    handleCloseReopenDialog: useCallback(() => setReopenDialogOpen(false), []),
    handleReopenSubmit,
    reopenStatusesQuery,

    // Task
    taskDialogOpen,
    taskSubmitting,
    handleOpenTaskDialog,
    handleCloseTaskDialog: useCallback(() => setTaskDialogOpen(false), []),
    handleTaskSubmit,
    taskTypesQuery,
  };
};
